import type { AdaptiveSubmit, JsonObject } from 'components/AdaptiveCard/AdaptiveCardRenderer';
import { TFunction } from 'i18next';
import { createAIActionBinding, toCopilotAction } from 'models/copilot/actions/aiActionBinding';
import { CopilotAction } from 'models/copilot/copilotAction';
import { CopilotActiveProcessContext } from 'models/copilot/copilotActiveProcessContext';
import { CopilotPanelContext } from 'models/copilot/copilotBridge';
import {
  CopilotConversation,
  CopilotConversationContentBlock,
  CopilotConversationItem,
  CopilotConversionItemType,
} from 'models/copilot/copilotConversation';
import {
  CopilotChatInputPlaceholder,
  CopilotPanelEntityRef,
  CopilotPanelProcessState,
  CopilotPanelProgressRef,
  CopilotPanelRuntime,
  CopilotPanelRuntimeView,
} from 'models/copilot/copilotPanelContextDefinition';
import { CopilotPreviewContent } from 'models/copilot/copilotPreviewContent';
import { KpiReportStage, TenantProcessStateKpiReport } from 'models/copilot/kpiReportProcessState';
import { TenantProcess, TenantProcessStatus } from 'models/copilot/tenantProcessStateBase';
import { EntityTypes } from 'models/entity';
import { apiCopilotEnsureKpiReportSession, apiCopilotExecuteProcessKpiReport } from 'services/Api/copilotService';
import { apiRequest } from 'services/Auth/authConfig';
import { CopilotAgentEvent, CopilotAgentEventKind } from 'services/SignalR/CopilotHub';
import { ProgressHubProcess } from 'services/SignalR/ProgressHub';
import { getCopilotProcessWelcomeMessage } from '../copilotProcessMessages';
import { kpiReportStartTemplate } from '../Generic/genericSceneAIActionTemplates';
import { getQuestionSetAnswerText, isQuestionSetCardSubmit } from '../Questions/questionSetCard';
import { CopilotKpiReportPanelState } from './copilotKpiReportPanelState';

/**
 * The KPI report as an AI process (PLAN-070), for the Generic usage that lists it on the KPIs scene and starts it from
 * the data report modal. Step 1 asks for what is missing with one question card and answers in the request; steps 2
 * and 3 run in the SVC under the data report job, followed by the progress bar keyed on the job; when the File card
 * lands the conversation goes back to the chat with the report context, so a follow-up turn can change the report.
 */
// The loop tools that start or reopen the process; their result (AIKpiReportStarted) names the row to follow
const handOffTools = ['kpi_report_start', 'kpi_report_regenerate'];

// While the data file is written the step text counts rows on every push: the state is read at most this often then,
// enough to see the PDF step begin; from the PDF step on every new step text (a chart, the summary) reloads it.
const dataStageReloadIntervalMs = 10000;
let lastDataStageReloadAt = 0;

export class KpiReportProcess {
  static isReportAction(action: CopilotAction | undefined): boolean {
    return action?.processId === TenantProcess.KpiReport;
  }

  static isRunning(runtime: Pick<CopilotPanelRuntimeView, 'action'>): boolean {
    return KpiReportProcess.isReportAction(runtime.action) && runtime.action?.status === TenantProcessStatus.InProgress;
  }

  static getPanelState(
    runtime: Pick<CopilotPanelRuntimeView, 'getUsageState'>,
  ): CopilotKpiReportPanelState | undefined {
    return runtime.getUsageState<CopilotKpiReportPanelState>(CopilotPanelContext.KpiReport);
  }

  static getState(runtime: Pick<CopilotPanelRuntimeView, 'getUsageState'>): TenantProcessStateKpiReport | undefined {
    return KpiReportProcess.getPanelState(runtime)?.state;
  }

  // the report the conversation is about after the hand-back; sent with every general knowledge turn
  static getActiveProcess(
    runtime: Pick<CopilotPanelRuntimeView, 'getUsageState'>,
  ): CopilotActiveProcessContext | undefined {
    return KpiReportProcess.getPanelState(runtime)?.activeProcess;
  }

  static getWelcomeMessage(action: CopilotAction, t: TFunction<string[]>): string | undefined {
    return KpiReportProcess.isReportAction(action) ? getCopilotProcessWelcomeMessage(action, t) : undefined;
  }

  // the inputs are answered on the card and the report is built without the user: no typing while the process runs
  static canStartChat(runtime: CopilotPanelRuntimeView): boolean {
    return !KpiReportProcess.isRunning(runtime);
  }

  static getChatInputPlaceholder(runtime: CopilotPanelRuntimeView): CopilotChatInputPlaceholder | undefined {
    if (!KpiReportProcess.isRunning(runtime)) return undefined;

    return KpiReportProcess.getState(runtime)?.stage === KpiReportStage.GatherInputs
      ? CopilotChatInputPlaceholder.AnswerCard
      : CopilotChatInputPlaceholder.Working;
  }

  // The data report job carries the run: no bar before the job exists (the card is the content). The bar follows the
  // row the server keyed on the job; its terminal push reloads the state, which brings the charts and the File card.
  static getProgressRef(runtime: CopilotPanelRuntimeView): CopilotPanelProgressRef | undefined {
    if (!KpiReportProcess.isRunning(runtime)) return undefined;

    const state = KpiReportProcess.getState(runtime);
    if (!state?.jobId || state.stage === KpiReportStage.GatherInputs || state.stage === KpiReportStage.Done) {
      return undefined;
    }

    return {
      entityType: EntityTypes.NotSet,
      entityId: KpiReportProcess.getRowKey(state),
      tenantProcess: TenantProcess.KpiReport,
      hubProcess: ProgressHubProcess.KpiDataReport,
      // the charts land one by one in the state conversation: a new step reloads it, so the pager grows as they come
      reloadsOnProgress: () => {
        if (state.stage === KpiReportStage.Pdf) return true;
        if (Date.now() - lastDataStageReloadAt < dataStageReloadIntervalMs) return false;

        lastDataStageReloadAt = Date.now();

        return true;
      },
    };
  }

  // Stop writes Canceled on the row the server keyed (a reservation, then the job), not on the scene's entity; the
  // row stays, the SVC worker reads its status to stop
  static getStopRef(runtime: CopilotPanelRuntimeView, action: CopilotAction): CopilotPanelEntityRef | undefined {
    if (!KpiReportProcess.isReportAction(action)) return undefined;

    const state = KpiReportProcess.getState(runtime);
    const entityId = state ? KpiReportProcess.getRowKey(state) : undefined;

    return entityId ? { entityType: EntityTypes.NotSet, entityId } : undefined;
  }

  // The action a typed start on any scene is followed under: the KPIs scene's own action, hidden from the lists
  static getFollowedAction(processId: TenantProcess, t: TFunction<string[]>): CopilotAction | undefined {
    if (processId !== TenantProcess.KpiReport) return undefined;

    const action = toCopilotAction(createAIActionBinding(kpiReportStartTemplate, undefined), t);
    if (action) action.hidden = true;

    return action;
  }

  /**
   * One execute: the start (with the job of the data report modal or the KPIs the scene had selected) or the answer
   * to the step-1 card. The answer replaces the state and the conversation; a report that is already Done hands
   * back at once.
   */
  static async execute(runtime: CopilotPanelRuntime): Promise<void> {
    const { action } = runtime;
    if (!action || !KpiReportProcess.isReportAction(action)) return;

    const previous = KpiReportProcess.getState(runtime);
    const startInput = previous ? undefined : action.startInput;

    runtime.setIsLoading(true);
    const answer = await apiCopilotExecuteProcessKpiReport(
      action.status || TenantProcessStatus.NotStarted,
      runtime.conversation,
      previous?.jobId ?? startInput?.jobId,
      previous ? undefined : startInput?.entityIds,
      previous?.reservationId,
      // the start only: the server makes the report's session under the panel's key
      previous ? undefined : runtime.copilotContextKey,
      // the start only: a surface that knows the format; otherwise the card asks for it
      previous ? undefined : startInput?.format,
      runtime.connectionId,
      runtime.accessToken,
    );
    if (!answer) return;

    runtime.setConversation((prev) => runtime.mergeAdaptiveInputs(prev, answer.conversation));
    KpiReportProcess.getPanelState(runtime)?.setState(answer);

    if (answer.stage === KpiReportStage.Done || answer.status === TenantProcessStatus.Completed) {
      await KpiReportProcess.handBack(runtime, answer);
    } else if (answer.status === TenantProcessStatus.Failed) {
      await runtime.endProcess(TenantProcess.KpiReport);
    }
  }

  // The step-1 card is this process's: the answers go onto the block (the server reads them there), a user bubble
  // words them, and the panel runs the execute.
  static onAdaptiveCardSubmit(
    runtime: CopilotPanelRuntime,
    block: CopilotConversationContentBlock,
    payload: AdaptiveSubmit<JsonObject>,
  ): boolean {
    if (!KpiReportProcess.isRunning(runtime) || !isQuestionSetCardSubmit(block, payload)) return false;

    for (const threadItem of runtime.conversation.thread) {
      const target = threadItem.contentBlocks?.find((candidate) => candidate.id === block.id);
      if (target) {
        target.adaptiveInputs = { ...(target.adaptiveInputs ?? {}), ...payload.inputs };
        break;
      }
    }

    const wording = getQuestionSetAnswerText(payload.inputs);
    if (wording) {
      const item = new CopilotConversationItem();
      item.type = CopilotConversionItemType.User;
      item.question = wording;
      runtime.conversation.thread.push(item);
    }
    runtime.setConversation(runtime.conversation.clone());

    return true;
  }

  // A reload of the persisted state (the terminal progress push): the charts and the File card of steps 2 and 3
  static applyProcessState(
    runtime: CopilotPanelRuntime,
    state: CopilotPanelProcessState,
  ): CopilotPreviewContent | undefined {
    if (!(state.data instanceof TenantProcessStateKpiReport)) return undefined;

    // the row names the report's session: the File card reaches its file through it
    if (state.data.sessionId) runtime.setCopilotSessionId(state.data.sessionId);
    KpiReportProcess.getPanelState(runtime)?.setState(state.data);
    // a regenerate with a new period or new KPIs runs under a new job: the context follows the row that is shown
    KpiReportProcess.setActiveProcess(runtime, state.data);
    runtime.setConversation((prev) => KpiReportProcess.spliceThread(prev, state.conversation));
    if (state.data.stage === KpiReportStage.Done || state.status === TenantProcessStatus.Completed) {
      void KpiReportProcess.handBack(runtime, state.data);
    } else if (state.status === TenantProcessStatus.Failed) {
      //a failed job ends the process here: the composer unlocks and the progress ref is not followed again
      void runtime.endProcess(TenantProcess.KpiReport);
    }

    return CopilotPreviewContent.None;
  }

  // The row a chat turn started or reopened (the typed start, a follow-up that regenerates the report): the entityId of
  // the tool result, or undefined for any other event
  static getHandOffEntityId(event: CopilotAgentEvent): number | undefined {
    if (event.kind !== CopilotAgentEventKind.ToolResult || !handOffTools.includes(event.toolName ?? ''))
      return undefined;

    const read = (value: unknown): number | undefined => {
      if (!value || typeof value !== 'object') return undefined;
      const record = value as { entityId?: unknown; result?: unknown };
      if (typeof record.entityId === 'number' && record.entityId !== 0) return record.entityId;

      return read(record.result);
    };

    const fromPayload = read(event.payload);
    if (fromPayload !== undefined || !event.resultPreview) return fromPayload;

    try {
      return read(JSON.parse(event.resultPreview));
    } catch {
      return undefined;
    }
  }

  static clearPreview(runtime: CopilotPanelRuntime): void {
    const panelState = KpiReportProcess.getPanelState(runtime);
    panelState?.setState(undefined);
    panelState?.setActiveProcess(undefined);
  }

  // The report is written: the process ends in the panel, its items (the card, the charts, the File card) stay in
  // the chat with one closing sentence, and the next turns carry the report as the active process.
  private static async handBack(runtime: CopilotPanelRuntime, state: TenantProcessStateKpiReport): Promise<void> {
    const handOffMessage = KpiReportProcess.getPanelState(runtime)?.handOffMessage;

    // ending the process clears the panel state, so the context is set after it
    await runtime.endProcess(TenantProcess.KpiReport);
    runtime.setConversation((prev) => {
      const conversation = KpiReportProcess.withoutRequest(KpiReportProcess.spliceThread(prev, state.conversation));
      // a second reload of the Done state finds the sentence already there
      if (handOffMessage && conversation.thread[conversation.thread.length - 1]?.response !== handOffMessage) {
        const closing = new CopilotConversationItem();
        closing.type = CopilotConversionItemType.Copilot;
        closing.response = handOffMessage;
        conversation.thread.push(closing);
      }

      return conversation;
    });
    KpiReportProcess.setActiveProcess(runtime, state);
    await KpiReportProcess.ensureSession(runtime, state);
  }

  // The File card reaches its file through the panel's session (PLAN-076). The Done row names the report's session
  // and the panel takes it. A row without one (written before the server stamped it) asks the server for it, made once
  // and kept on the row, unless the panel already has a session; a failed call is shown at the bottom of the panel.
  private static async ensureSession(runtime: CopilotPanelRuntime, state: TenantProcessStateKpiReport): Promise<void> {
    if (state.sessionId) {
      runtime.setCopilotSessionId(state.sessionId);

      return;
    }
    if (runtime.copilotSessionId || !state.jobId || !runtime.copilotContextKey) return;

    try {
      // a reload's runtime carries no access token: the call takes its own
      const accessToken = await runtime.appContext.getAccessToken(apiRequest.scopes);
      runtime.setCopilotSessionId(
        await apiCopilotEnsureKpiReportSession(state.jobId, runtime.copilotContextKey, accessToken),
      );
    } catch {
      const message = KpiReportProcess.getPanelState(runtime)?.sessionFailedMessage;
      if (message) runtime.appContext.showNotification(message, true, false, true);
    }
  }

  // The process thread spliced into the chat the panel shows (a typed start, a regenerate, a reload): an item the
  // chat holds already (the same id, or one of its blocks, as the File card a new version replaces) is replaced where
  // it stands, a new one is appended; the user's questions and the answers around them stay.
  private static spliceThread(
    chat: CopilotConversation | undefined,
    process: CopilotConversation,
  ): CopilotConversation {
    if (!chat || chat.thread.length === 0) return process.clone();

    const conversation = chat.clone();
    for (const item of process.thread) {
      const blockIds = (item.contentBlocks ?? []).map((block) => block.id).filter((id) => !!id);
      const index = conversation.thread.findIndex(
        (existing) =>
          (!!item.id && existing.id === item.id) ||
          (existing.contentBlocks ?? []).some((block) => !!block.id && blockIds.includes(block.id)),
      );
      if (index >= 0) {
        conversation.thread[index] = item.clone();
      } else {
        conversation.thread.push(item.clone());
      }
    }

    return conversation;
  }

  // The row key: the server re-keys the row on the job once it exists, a reservation-keyed copy may still hold the
  // reservation then
  private static getRowKey(state: TenantProcessStateKpiReport): number {
    return state.entityId > 0 ? state.entityId : state.jobId ?? state.entityId;
  }

  // The report the next general knowledge turns are about: the job is the entity, the reservation the key, which an
  // MCP-started job does not have and then is not sent; a failed or stopped run keeps no context.
  private static setActiveProcess(runtime: CopilotPanelRuntime, state: TenantProcessStateKpiReport): void {
    if (!state.jobId || state.status === TenantProcessStatus.Failed || state.status === TenantProcessStatus.Cancelled) {
      return;
    }

    KpiReportProcess.getPanelState(runtime)?.setActiveProcess({
      processId: TenantProcess.KpiReport,
      key: state.reservationId || undefined,
      entityId: state.jobId,
    });
  }

  private static withoutRequest(conversation: CopilotConversation): CopilotConversation {
    conversation.request = undefined;

    return conversation;
  }
}
